/**
 * Sanitizes SSE lines from the chat stream before the AI SDK validates them.
 *
 * The backend runs `ai@6.0.217` while this app is pinned to `ai@6.0.0-beta.92`.
 * The newer serializer adds fields the pinned client's chunk schemas reject —
 * they are `z.strictObject`, so a single unrecognized key throws an
 * AI_TypeValidationError that tears down the whole stream mid-message. The user
 * sees "We're having trouble connecting to our AI service" and loses everything
 * after the offending chunk, even though the backend answered fine.
 *
 * The offender behind the reported incident is `providerMetadata` on tool
 * chunks, which Anthropic populates with caller info on every tool result — so
 * any turn where the model actually ran a tool broke. It is not the only one:
 * UNSUPPORTED_FIELDS_BY_TYPE below covers the whole schema delta rather than
 * only the chunk we caught in the act.
 *
 * This is a compatibility shim. The real fix is aligning the frontend and
 * backend AI SDK versions, after which this module and both call sites should
 * be deleted.
 */

/**
 * Fields the backend's serializer emits that our pinned client rejects, keyed
 * by chunk type.
 *
 * Produced by diffing the `UIMessageChunk` union between the two installed
 * versions, so it is exhaustive for this version pair rather than a list of the
 * symptoms we happened to hit in production. Every field here is optional on
 * the backend side, which is why deleting it leaves a chunk the client
 * validates and renders exactly as it would its own.
 *
 * The mapping is deliberately per-type rather than a blanket field list:
 * `providerMetadata` is perfectly legal on `tool-input-available`,
 * `tool-input-error`, and every text/reasoning/source/file chunk, so stripping
 * it everywhere would discard data the client is happy to accept.
 */
const UNSUPPORTED_FIELDS_BY_TYPE: Readonly<Record<string, readonly string[]>> = {
  // Anthropic populates providerMetadata with caller info on every tool result.
  "tool-input-start": ["providerMetadata", "toolMetadata"],
  "tool-input-available": ["toolMetadata"],
  "tool-input-error": ["toolMetadata"],
  "tool-output-available": ["providerMetadata", "toolMetadata"],
  "tool-output-error": ["providerMetadata", "toolMetadata"],
  "tool-approval-request": ["signature"],
  // Stream lifecycle. `abort` is reachable on any turn the backend's own
  // 120s watchdog fires, and it always carries a reason: calling
  // AbortController.abort() with no argument still sets signal.reason.
  finish: ["finishReason"],
  abort: ["reason"],
};

const DATA_PREFIX = "data: ";

interface StreamChunk {
  type: string;
  [key: string]: unknown;
}

function isStreamChunk(value: unknown): value is StreamChunk {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

/**
 * @param line A single line from the SSE response.
 * @returns The line to forward to the AI SDK.
 *
 * Only ever rewrites, never drops. Every incompatibility in this version pair
 * is an extra *field* on an otherwise valid chunk, so removing the field is
 * enough — dropping the chunk would also throw away the parts the client wants
 * (a `finish` still carries `messageMetadata`, and the step boundaries of a
 * multi-step tool turn are what delimit its rendering).
 *
 * Anything this function cannot confidently parse — blank lines, comments, and
 * partial lines split across network chunks — is returned unchanged, so the
 * stream stays byte-identical unless there is a known incompatibility to fix.
 */
export function sanitizeStreamLine(line: string): string {
  if (!line.startsWith(DATA_PREFIX)) {
    return line;
  }

  const payload = line.slice(DATA_PREFIX.length);

  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    // Not complete JSON yet (chunk boundary) or the [DONE] sentinel — leave it be.
    return line;
  }

  if (!isStreamChunk(parsed)) {
    return line;
  }

  // Bind to a const: TypeScript discards narrowing of a `let` inside closures.
  const chunk = parsed;

  const unsupported = UNSUPPORTED_FIELDS_BY_TYPE[chunk.type];
  if (!unsupported) {
    return line;
  }

  const present = unsupported.filter((field) => field in chunk);
  if (present.length === 0) {
    return line;
  }

  const sanitized: StreamChunk = { ...chunk };
  for (const field of present) {
    delete sanitized[field];
  }

  return `${DATA_PREFIX}${JSON.stringify(sanitized)}`;
}

/**
 * Wraps a streaming `Response` so every SSE line passes through
 * {@link sanitizeStreamLine} before the AI SDK parses it.
 *
 * Network chunks split at arbitrary byte offsets, routinely mid-line. Lines are
 * therefore buffered and only handed to the sanitizer once complete — sanitizing
 * a half-line would silently miss the incompatible field and let the stream blow
 * up again, which is exactly the failure this module exists to prevent.
 *
 * The result is line-for-line lossless: every line in, one line out, blank
 * event terminators included.
 */
export function sanitizeStreamResponse(response: Response): Response {
  if (!response.body) {
    return response;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();

  const sanitizedStream = new ReadableStream<Uint8Array>({
    async start(controller) {
      // Holds the trailing partial line between reads.
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // Flush whatever the stream ended on, without inventing a newline
            // the original stream did not have.
            if (buffer) {
              controller.enqueue(encoder.encode(sanitizeStreamLine(buffer)));
            }
            controller.close();
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          // The last element is either an incomplete line or "" when the chunk
          // ended on a newline. Either way it is not safe to process yet.
          buffer = lines.pop() ?? "";

          // Blank lines are the event terminators SSE dispatches on, so they
          // are re-emitted like any other line — swallowing one would glue two
          // events together.
          if (lines.length > 0) {
            const sanitized = lines.map(sanitizeStreamLine);
            controller.enqueue(encoder.encode(`${sanitized.join("\n")}\n`));
          }
        }
      } catch (error) {
        controller.error(error);
      }
    },
  });

  return new Response(sanitizedStream, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}
